/**
 * web/src/shell/bridge.ts — PARENT-OWNED (W0). The only way feature UI
 * modules talk to the app: API base, session, auth headers.
 *
 * Feature modules (web/src/requests/ui.ts, web/src/questions/ui.ts, …) import
 * from here and export a `mount(container: HTMLElement)` function; the parent
 * wires nav + mount calls in main.ts / index.html during integration (W3).
 * Children must NOT edit this file or main.ts.
 */

/** Empty in dev (vite proxies /api + /ws), set at build time for Pages. */
export const API_BASE: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env?.VITE_API_BASE) || '';

const LS_TOKEN = 'pear_token';
const LS_COUPLE_ID = 'pear_couple_id';
const LS_ROLE = 'pear_role';
const LS_MEMBER_ID = 'pear_member_id';

export function getToken(): string | null {
  return localStorage.getItem(LS_TOKEN);
}

export interface Session {
  token: string;
  coupleId: string;
  role: 'A' | 'B';
  memberId: string;
}

export function session(): Session | null {
  const token = localStorage.getItem(LS_TOKEN);
  const coupleId = localStorage.getItem(LS_COUPLE_ID);
  const role = localStorage.getItem(LS_ROLE);
  const memberId = localStorage.getItem(LS_MEMBER_ID);
  if (!token || !coupleId || !role || !memberId) return null;
  return { token, coupleId, role: role as 'A' | 'B', memberId };
}

/** Page-lifetime abort. Navigating away (a reload, a hard redirect) kills every
 *  request still in flight, and the browser reports that as a bare
 *  `TypeError: Failed to fetch` — indistinguishable from a real network fault.
 *  Aborting the requests OURSELVES on pagehide converts that unclassifiable
 *  failure into a real AbortError, so a screen the user just left can stay
 *  quiet instead of logging its own teardown as an outage (card d0Md65M0shSI). */
const pageAbort = new AbortController();
if (typeof window !== 'undefined' && typeof pageAbort.signal.addEventListener === 'function') {
  window.addEventListener('pagehide', () => pageAbort.abort(new DOMException('page teardown', 'AbortError')));
}

/** True when `err` is a cancelled request — page teardown, or a caller's own
 *  signal — rather than something the user should be told about. Callers use it
 *  to keep a screen they just left from logging a failure (see InviteCard). */
export function isAbort(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

/** Link a caller's signal to the page-lifetime one, so BOTH kinds of teardown
 *  produce a real AbortError. Hand-rolled because web/tsconfig targets ES2020
 *  and has no AbortSignal.any. */
function linkSignal(signal?: AbortSignal): AbortSignal {
  const page = pageAbort.signal;
  if (!signal) return page;
  if (signal === page) return page;
  const ctrl = new AbortController();
  const forward = () => { if (!ctrl.signal.aborted) ctrl.abort(signal.reason ?? page.reason); };
  if (signal.aborted || page.aborted) forward();
  else {
    signal.addEventListener('abort', forward, { once: true });
    page.addEventListener('abort', forward, { once: true });
  }
  return ctrl.signal;
}

/** JSON fetch against the worker. Throws Error(JSON.stringify(body)) on non-2xx.
 *  `signal` lets a caller cancel in-flight work (sheet teardown aborts its own
 *  request); the server may still have saved — never treat abort as a delete. */
export async function api<T = unknown>(
  path: string,
  opts?: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: Record<string, unknown>; signal?: AbortSignal; timeoutMs?: number },
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const token = getToken();
  if (token) headers['authorization'] = `Bearer ${token}`;
  /* A read that never settles is worse than a read that fails: the caller's
     `items === null` skeleton (Requests) shimmers for ever with no error state
     and no retry (card 5f1XfZmgqtdr). `timeoutMs` is opt-in, so no existing
     caller changes behaviour; a timeout surfaces as a real failure, never as an
     AbortError, because isAbort() is the "user left the screen" guard. */
  const outer = linkSignal(opts?.signal);
  let timedOut = false;
  let signal = outer;
  let timer: ReturnType<typeof setTimeout> | undefined;
  if (opts?.timeoutMs) {
    const ctrl = new AbortController();
    const forward = () => { if (!ctrl.signal.aborted) ctrl.abort(outer.reason); };
    if (outer.aborted) forward();
    else outer.addEventListener('abort', forward, { once: true });
    timer = setTimeout(() => {
      timedOut = true;
      if (!ctrl.signal.aborted) ctrl.abort(new DOMException('request timeout', 'AbortError'));
    }, opts.timeoutMs);
    signal = ctrl.signal;
  }
  try {
    const res = await fetch(API_BASE + path, {
      method: opts?.body ? (opts.method ?? 'POST') : (opts?.method ?? 'GET'),
      headers,
      body: opts?.body ? JSON.stringify(opts.body) : undefined,
      signal,
    });
    // Read as text first. A 2xx with no body (204) is legitimately empty and stays
    // null; a 2xx whose body we could NOT read is a torn-down request, and
    // returning null to a Promise<T> caller is a null-deref downstream
    // (Settings did `data.enabled` on it — card d0Md65M0shSI).
    let raw: string | null;
    try {
      raw = await res.text();
    } catch (e) {
      if (isAbort(e) || res.status === 204 || res.status === 205) throw e;
      throw new Error(JSON.stringify({ status: res.status, unreadable_body: true }));
    }
    if (!res.ok) {
      let parsed: unknown = null;
      try { parsed = raw ? JSON.parse(raw) : null; } catch { /* non-JSON error body */ }
      throw new Error(JSON.stringify(parsed ?? { status: res.status }));
    }
    if (raw === null || raw === '') return null as T;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null as T;
    }
  } catch (e) {
    /* A caller-mandated timeout is a FAILURE, not a teardown: report it as one
       so the screen paints its error state instead of waiting for ever. */
    if (timedOut) throw new Error(JSON.stringify({ timeout: true, path, timeoutMs: opts?.timeoutMs }));
    throw e;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Escape for innerHTML interpolation (feature UIs build markup as strings). */
export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}
